import axios, { AxiosInstance, AxiosResponse } from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { DEPARTAMENTOS_TEMP, MUNICIPIOS_TEMP } from '../data/departamentosTemp';
import {
  Product,
  Category,
  PaymentMethod,
  CheckoutData,
  Order,
  User
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

// Interfaces para el departamento y municipio
export interface Departamento {
  id: number;
  nombre: string;
}

export interface Municipio {
  id: number;
  nombre: string;
  codigo_postal?: string;
}

// Interfaz para zona
export interface Zona {
  id: number;
  codigo_zona: number;
  idmunicipio: number;
  nombre: string;
}

// Interfaces para marca y modelo
export interface Marca {
  id: number;
  nombre: string;
}

export interface Modelo {
  id: number;
  nombre: string;
  marca_id: number;
}

// Interfaz para el formulario de registro
export interface ClienteRegisterForm {
  nombre: string;
  telefono: string;
  correo: string;
  marca?: Marca;
  modelo?: Modelo;
  referido_por?: number;
  departamento?: Departamento;
  municipio?: Municipio;
  zona?: number;
  linea?: number;
  nit?: string;
  edad?: number;
  dia_cumple?: number;
  mes_cumple?: number;
  password: string;
  password_confirmation: string;
}

class APIService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Detectar ambiente automáticamente
    const isWeb = typeof window !== 'undefined';
    const isDevelopment = isWeb && (
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1'
    );
    
    // Usar URL de producción o desarrollo según el ambiente
    this.baseURL = isDevelopment 
      ? 'http://127.0.0.1:8000/api'  // Desarrollo local
      : 'https://fideliza.motomarket.com.gt/api';  // Producción
    
    console.log('🌐 APIService inicializado con baseURL:', this.baseURL);

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Obtiene la URL base del API
   */
  getBaseURL(): string {
    return this.baseURL;
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(
      async (config) => {
        const token = await AsyncStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          AsyncStorage.removeItem('auth_token');
          AsyncStorage.removeItem('user_data');
        }
        return Promise.reject(error);
      }
    );
  }

  // PRODUCTOS - SOLO BACKEND (MIGRADO A LÍNEAS)
  async getProducts(params?: any): Promise<{ data: Product[]; total?: number }> {
    console.log('🔍 SOLO BACKEND: Obteniendo productos con parámetros (sistema líneas):', params);
    
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams();
    
    if (params) {
      if (params.search) queryParams.append('search', params.search);
      if (params.linea_id) queryParams.append('linea_id', params.linea_id.toString());
      if (params.categoria) queryParams.append('linea_id', params.categoria); // Compatibilidad temporal
      if (params.marca_id) queryParams.append('marca_id', params.marca_id.toString());
      if (params.modelo_id) queryParams.append('modelo_id', params.modelo_id.toString());
      if (params.min_price) queryParams.append('min_price', params.min_price.toString());
      if (params.max_price) queryParams.append('max_price', params.max_price.toString());
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.per_page) queryParams.append('per_page', params.per_page.toString());
    }

    const endpoint = queryParams.toString() 
      ? `/mobile/products?${queryParams.toString()}`
      : '/mobile/products';
    
    console.log('🔍 SOLO BACKEND: Endpoint final:', `${this.baseURL}${endpoint}`);
    
    const response = await this.api.get<ApiResponse<Product[]>>(endpoint);
    
    console.log('🔍 SOLO BACKEND: Respuesta del servidor:', response.data);
    
    if (response.data.success && response.data.data) {
      return {
        data: response.data.data,
        total: response.data.total || response.data.data.length
      };
    }
    
    throw new Error('Error al obtener productos del backend');
  }

  // LÍNEAS - SOLO BACKEND (REEMPLAZA CATEGORÍAS)
  async getCategories(): Promise<Category[]> {
    console.log('🏷️ SOLO BACKEND: Obteniendo líneas del backend Laravel (reemplaza categorías)');
    console.log('🏷️ SOLO BACKEND: Endpoint:', `${this.baseURL}/test/lineas-directas`);
    
    try {
      // Usar directamente el endpoint que funciona
      const response = await this.api.get<any>('/test/lineas-directas');
      console.log('🏷️ SOLO BACKEND: Respuesta del servidor:', response.data);
      
      // Convertir formato si es necesario
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const lineas = response.data.data.map((linea: any) => ({
          id: linea.id,
          descripcion: linea.descripcion,
          idempresa: linea.idempresa || 30
        }));
        console.log('🏷️ SOLO BACKEND: Líneas procesadas:', lineas);
        return lineas;
      }
      
      throw new Error('Formato de respuesta no válido');
    } catch (error) {
      console.error('❌ Error obteniendo líneas del backend:', error);
      throw new Error('Error al obtener líneas del backend');
    }
  }

  // DEPARTAMENTOS - SOLO BACKEND CON FALLBACK TEMPORAL
  async getDepartamentos(): Promise<any[]> {
    console.log('🏛️ SOLO BACKEND: Obteniendo departamentos de Guatemala');
    console.log('🏛️ SOLO BACKEND: Endpoint:', `${this.baseURL}/departamentos`);
    
    try {
      const response = await this.api.get<ApiResponse<any[]>>('/departamentos');
      
      console.log('🏛️ SOLO BACKEND: Respuesta del servidor:', response.data);
      
      if (response.data.success && response.data.data) {
        console.log('🏛️ SOLO BACKEND: Departamentos obtenidos:', response.data.data.length);
        return response.data.data;
      }
      
      throw new Error('Error al obtener departamentos del backend');
    } catch (error) {
      console.error('❌ Error obteniendo departamentos del backend, usando datos temporales:', error);
      
      // Fallback: usar datos temporales de Guatemala
      console.log('🔄 Usando datos temporales de departamentos de Guatemala (' + DEPARTAMENTOS_TEMP.length + ' departamentos)');
      return DEPARTAMENTOS_TEMP;
    }
  }

  // MUNICIPIOS POR DEPARTAMENTO - SOLO BACKEND CON FALLBACK TEMPORAL
  async getMunicipios(departamentoId: number): Promise<any[]> {
    console.log('🏛️ SOLO BACKEND: Obteniendo municipios para departamento:', departamentoId);
    
    try {
      const response = await this.api.get<ApiResponse<any[]>>(`/mobile/municipios/${departamentoId}`);
      
      console.log('🏛️ SOLO BACKEND: Respuesta de municipios:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return []; // Retornar array vacío si no hay municipios
    } catch (error) {
      console.error('❌ Error obteniendo municipios del backend, usando datos temporales:', error);
      
      // Fallback: usar datos temporales
      const municipios = MUNICIPIOS_TEMP[departamentoId] || [];
      console.log(`🔄 Usando datos temporales de municipios para departamento ${departamentoId} (${municipios.length} municipios)`);
      return municipios;
    }
  }

  // ZONAS POR MUNICIPIO - SOLO BACKEND
  async getZonas(municipioId: number): Promise<Zona[]> {
    console.log('📍 SOLO BACKEND: Obteniendo zonas para municipio:', municipioId);
    
    try {
      const response = await this.api.get<ApiResponse<Zona[]>>(`/mobile/zonas/${municipioId}`);
      
      console.log('📍 SOLO BACKEND: Respuesta de zonas:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return []; // Retornar array vacío si no hay zonas
    } catch (error) {
      console.error('❌ Error obteniendo zonas del backend:', error);
      return [];
    }
  }

  // MARCAS DE MOTOS - SOLO BACKEND
  async getMarcas(): Promise<Marca[]> {
    console.log('🏍️ SOLO BACKEND: Obteniendo marcas de motos');
    
    try {
      const response = await this.api.get<ApiResponse<any[]>>('/mobile/marcas');
      
      console.log('🏍️ SOLO BACKEND: Respuesta de marcas:', response.data);
      
      if (response.data.success && response.data.data) {
        // Normalizar IDs a números
        const marcas = response.data.data.map((marca: any) => ({
          id: parseInt(marca.id),
          nombre: marca.nombre
        }));
        console.log('🏍️ Marcas normalizadas:', marcas);
        return marcas;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Error obteniendo marcas del backend:', error);
      return [];
    }
  }

  // MODELOS DE MOTOS POR MARCA - SOLO BACKEND
  async getModelos(marcaId: number): Promise<Modelo[]> {
    console.log('🏍️ SOLO BACKEND: Obteniendo modelos para marca:', marcaId);
    
    try {
      const response = await this.api.get<ApiResponse<any[]>>(`/mobile/modelos/${marcaId}`);
      
      console.log('🏍️ SOLO BACKEND: Respuesta de modelos:', response.data);
      
      if (response.data.success && response.data.data) {
        // Normalizar IDs a números
        const modelos = response.data.data.map((modelo: any) => ({
          id: parseInt(modelo.id),
          nombre: modelo.nombre,
          marca_id: parseInt(modelo.marca_id)
        }));
        console.log('🏍️ Modelos normalizados:', modelos);
        return modelos;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Error obteniendo modelos del backend:', error);
      return [];
    }
  }

  // CLIENTES - SOLO BACKEND
  async getClientes(): Promise<{ id: number; nombre: string; telefono: string; nit?: string }[]> {
    console.log('👥 SOLO BACKEND: Obteniendo lista de clientes');
    
    try {
      const response = await this.api.get<ApiResponse<any[]>>('/mobile/clientes');
      
      console.log('👥 SOLO BACKEND: Respuesta de clientes:', response.data);
      
      if (response.data.success && response.data.data) {
        const clientes = response.data.data.map((cliente: any) => ({
          id: parseInt(cliente.id),
          nombre: cliente.nombre,
          telefono: cliente.telefono,
          nit: cliente.nit || ''
        }));
        console.log('👥 Clientes cargados:', clientes.length);
        return clientes;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Error obteniendo clientes del backend:', error);
      return [];
    }
  }

  // BÚSQUEDA - SOLO BACKEND
  async searchProducts(query: string): Promise<Product[]> {
    console.log('🔍 SOLO BACKEND: Buscando productos con query:', query);
    console.log('🔍 SOLO BACKEND: Endpoint:', `${this.baseURL}/mobile/products/search?q=${encodeURIComponent(query)}`);
    
    const response = await this.api.get<ApiResponse<Product[]>>(`/mobile/products/search?q=${encodeURIComponent(query)}`);
    
    console.log('🔍 SOLO BACKEND: Respuesta de búsqueda:', response.data);
    
    if (response.data.success && response.data.data) {
      console.log('🔍 SOLO BACKEND: Productos encontrados:', response.data.data.length);
      return response.data.data;
    }
    
    throw new Error('Error al buscar productos en el backend');
  }

  // AUTENTICACIÓN
  async login(credentials: { login: string; password: string }): Promise<{ user: User; token: string }> {
    console.log('🔐 SOLO BACKEND: Realizando login para:', { login: credentials.login, password: '***' });
    try {
      const response = await this.api.post<ApiResponse<{ user: any; token: string }>>('/auth/login', {
        login: credentials.login,
        password: credentials.password,
      });

      console.log('🔐 SOLO BACKEND: Respuesta de login:', response.data);
      console.log('🔐 SOLO BACKEND: Status:', response.status);
    
      if (response.data.success && response.data.data) {
        const { user: rawUser, token } = response.data.data;
        // Normalizar datos del usuario
        const user: User = {
          id: rawUser.id || rawUser.cliente_id,
          cliente_id: rawUser.cliente_id || rawUser.id,
          name: rawUser.name || rawUser.nombre, // Mapear nombre → name
          email: rawUser.email || rawUser.correo,
          telefono: rawUser.telefono || rawUser.phone,
          direccion: rawUser.direccion,
          identificacion: rawUser.identificacion,
          departamento: rawUser.departamento,
          municipio: rawUser.municipio,
          nit: rawUser.nit,
          edad: rawUser.edad,
        };
        console.log('👤 Usuario normalizado después del login:', user);
        await AsyncStorage.setItem('auth_token', token);
        await AsyncStorage.setItem('user_data', JSON.stringify(user));
        return { user, token };
      }

      throw new Error(response.data.message || 'Error en el login');
    } catch (error: any) {
      console.error('❌ Error en login:', error.message);
      console.error('❌ Error completo:', error);
      console.error('❌ Response data:', error.response?.data);
      console.error('❌ Status code:', error.response?.status);
      throw error;
    }
  }

  async register(userData: ClienteRegisterForm): Promise<{ user: User; token: string }> {
    console.log('📝 SOLO BACKEND: Registrando nuevo usuario');
    
    // Convertir objetos a strings para el backend y filtrar undefined
    const backendData: any = {
      nombre: userData.nombre,
      telefono: userData.telefono,
      correo: userData.correo,
      password: userData.password,
    };
    
    // Agregar campos opcionales solo si tienen valor
    if (userData.marca?.nombre) backendData.marca = userData.marca.nombre;
    if (userData.modelo?.nombre) backendData.modelo = userData.modelo.nombre;
    if (userData.nit) backendData.nit = userData.nit;
    if (userData.edad) backendData.edad = userData.edad;
    if (userData.dia_cumple) backendData.dia_cumple = userData.dia_cumple;
    if (userData.mes_cumple) backendData.mes_cumple = userData.mes_cumple;
    if (userData.referido_por) backendData.referido_por = userData.referido_por;
    if (userData.departamento?.id) backendData.departamento = userData.departamento.id;
    if (userData.municipio?.id) backendData.municipio = userData.municipio.id;
    if (userData.zona) backendData.zona = userData.zona;
    
    console.log('📝 Datos enviados al backend:', backendData);
    
    const response = await this.api.post<ApiResponse<{ user: User; token: string }>>('/auth/register', backendData);
    
    console.log('📝 SOLO BACKEND: Respuesta de registro:', response.data);

    if (response.data.success && response.data.data) {
      const { user, token } = response.data.data;
      // NO guardar automáticamente el token después del registro
      // El usuario debe hacer login manualmente
      console.log('📝 Registro completado, usuario debe hacer login manualmente');
      return { user, token };
    }

    throw new Error(response.data.message || 'Error en el registro');
  }

  async logout(): Promise<void> {
    console.log('🚪 APIService: Cerrando sesión');
    
    try {
      // Obtener user_data antes de limpiar para saber qué carrito eliminar
      const userData = await AsyncStorage.getItem('user_data');
      const user = userData ? JSON.parse(userData) : null;
      
      // Intentar cerrar sesión en el backend
      try {
        await this.api.post('/auth/logout');
      } catch (error) {
        console.warn('⚠️ APIService: Error al cerrar sesión en el backend, limpiando datos locales');
      }
      
      // Limpiar datos de autenticación
      await AsyncStorage.removeItem('auth_token');
      await AsyncStorage.removeItem('user_data');
      
      // Limpiar carrito del usuario
      if (user?.id || user?.cliente_id) {
        const userId = user.id || user.cliente_id;
        const cartKey = `persistent_cart_user_${userId}`;
        await AsyncStorage.removeItem(cartKey);
        console.log('🛒 APIService: Carrito limpiado para usuario:', userId);
      }
      
      // Limpiar carrito de invitado también por seguridad
      await AsyncStorage.removeItem('persistent_cart_guest');
      
      console.log('✅ APIService: Sesión cerrada completamente');
    } catch (error) {
      console.error('❌ APIService: Error durante el logout:', error);
      // Forzar limpieza incluso si hay error
      await AsyncStorage.removeItem('auth_token');
      await AsyncStorage.removeItem('user_data');
      await AsyncStorage.removeItem('persistent_cart_guest');
    }
  }

  async getStoredAuthData(): Promise<{ token: string | null; user: any | null }> {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      const userData = await AsyncStorage.getItem('user_data');
      
      console.log('🔐 APIService: Token almacenado:', token ? 'Existe' : 'No existe');
      console.log('🔐 APIService: Datos de usuario almacenados:', userData ? 'Existen' : 'No existen');
      
      if (userData) {
        const user = JSON.parse(userData);
        console.log('🔐 APIService: Usuario recuperado:', user.name || user.email);
        return { token, user };
      }
      
      return { token, user: null };
    } catch (error) {
      console.error('🔐 APIService: Error recuperando datos almacenados:', error);
      return { token: null, user: null };
    }
  }

  async getCurrentUser(): Promise<User> {
    console.log('👤 SOLO BACKEND: Obteniendo información del usuario actual');
    
    const response = await this.api.get<ApiResponse<any>>('/auth/me');
    
    console.log('👤 SOLO BACKEND: Respuesta de usuario actual:', response.data);
    
    if (response.data.success && response.data.data) {
      // Normalizar datos del usuario: backend puede enviar "nombre" y necesitamos "name"
      const userData = response.data.data;
      const normalizedUser: User = {
        id: userData.id || userData.cliente_id,
        cliente_id: userData.cliente_id || userData.id,
        name: userData.name || userData.nombre, // Mapear nombre → name
        email: userData.email || userData.correo,
        telefono: userData.telefono || userData.phone,
        direccion: userData.direccion,
        identificacion: userData.identificacion,
        departamento: userData.departamento,
        municipio: userData.municipio,
        nit: userData.nit,
        edad: userData.edad,
        monto_acumulado: userData.monto_acumulado,
        fecha_registro: userData.fecha_registro,
        estado: userData.estado,
        tipo_cliente: userData.tipo_cliente,
      };
      
      console.log('👤 Usuario normalizado:', normalizedUser);
      
      // Actualizar datos almacenados localmente
      await AsyncStorage.setItem('user_data', JSON.stringify(normalizedUser));
      return normalizedUser;
    }
    
    throw new Error(response.data.message || 'Error al obtener información del usuario');
  }

  async updateProfile(profileData: Partial<User>): Promise<User> {
    console.log('👤 SOLO BACKEND: Actualizando perfil del usuario');
    console.log('👤 SOLO BACKEND: Datos a actualizar:', profileData);
    
    // Mapear campos del frontend al backend
    const backendData: any = {};
    
    if (profileData.name) backendData.nombre = profileData.name;
    if (profileData.telefono) backendData.telefono = profileData.telefono;
    if (profileData.phone) backendData.telefono = profileData.phone; // Compatibilidad
    if (profileData.direccion) backendData.direccion = profileData.direccion;
    if (profileData.identificacion) backendData.identificacion = profileData.identificacion;
    if (profileData.nit) backendData.nit = profileData.nit;
    if (profileData.edad) backendData.edad = profileData.edad;
    
    const response = await this.api.put<ApiResponse<{ user: User }>>('/auth/profile', backendData);
    
    console.log('👤 SOLO BACKEND: Respuesta de actualización de perfil:', response.data);
    
    if (response.data.success && response.data.data && response.data.data.user) {
      const updatedUser = response.data.data.user;
      // Actualizar datos almacenados localmente
      await AsyncStorage.setItem('user_data', JSON.stringify(updatedUser));
      return updatedUser;
    }
    
    throw new Error(response.data.message || 'Error al actualizar el perfil');
  }

  // OBTENER REFERIDOS DEL CLIENTE
  async getReferidos(): Promise<any[]> {
    console.log('👥 BACKEND: Obteniendo lista de referidos');
    
    try {
      // Obtener cliente_id almacenado
      const authData = await this.getStoredAuthData();
      const clienteId = authData?.user?.id || authData?.user?.cliente_id;
      
      console.log('👥 Auth data:', authData);
      console.log('👥 Cliente ID extraído:', clienteId);
      
      if (!clienteId) {
        throw new Error('No se encontró el ID del cliente. Por favor, inicie sesión nuevamente.');
      }
      
      console.log('👥 Consultando referidos para cliente ID:', clienteId);
      
      const response = await this.api.get<ApiResponse<any[]>>('/auth/referidos', {
        params: { cliente_id: clienteId }
      });
      
      console.log('👥 Respuesta de referidos:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return [];
    } catch (error: any) {
      console.error('❌ Error obteniendo referidos:', error);
      throw new Error(error.response?.data?.message || 'Error al obtener referidos');
    }
  }

  // FAVORITOS - MÉTODO PLACEHOLDER
  async toggleFavorite(productId: number): Promise<void> {
    console.log('❤️ Toggle favorite para producto:', productId);
    // Por ahora solo log, implementar endpoint cuando esté disponible
    return Promise.resolve();
  }

  // CHECKOUT - SOLO BACKEND
  async processCheckout(checkoutData: CheckoutData): Promise<Order> {
    console.log('🛒 SOLO BACKEND: Procesando checkout');
    console.log('🛒 SOLO BACKEND: Datos del pedido:', checkoutData);
    
    // Convertir datos al formato esperado por el backend
    const backendData = {
      items: checkoutData.items.map((item: any, index) => {
        console.log(`🔍 Item ${index}:`, item);
        console.log(`🔍 Item ${index} keys:`, Object.keys(item));
        
        // Normalizar estructura: el carrito usa id_producto, cantidad, precio_unitario
        const productId = item.id_producto || item.product_id || item.id || item.product?.id;
        const quantity = item.cantidad || item.quantity || 1;
        const price = item.precio_unitario || item.price || item.product?.precio || 0;
        
        console.log(`✅ Item ${index} normalizado - product_id: ${productId}, quantity: ${quantity}, price: ${price}`);
        
        return {
          product_id: productId,
          quantity: quantity,
          price: price
        };
      }),
      shipping_data: {
        name: checkoutData.shippingAddress?.name || '',
        address: checkoutData.shippingAddress?.address || '',
        city: checkoutData.shippingAddress?.city || '',
        phone: checkoutData.shippingAddress?.phone || '',
        department: checkoutData.shippingAddress?.department || 'Guatemala',
        municipio: checkoutData.shippingAddress?.municipio || '',
        references: checkoutData.shippingAddress?.references || ''
      },
      payment_method_id: checkoutData.paymentMethod?.id_formapago || 1,
      complementary_payment_method_id: checkoutData.complementaryPaymentMethod?.id_formapago || null,
      wallet_payment: checkoutData.walletPayment || 0,
      complementary_payment: checkoutData.complementaryPayment || 0,
      total: checkoutData.total,
      subtotal: checkoutData.subtotal || checkoutData.total,
      taxes: checkoutData.taxes || 0,
      shipping: checkoutData.shipping || 0,
      notes: checkoutData.notes || 'Pedido desde MotoMarketGT App'
    };

    console.log('🛒 SOLO BACKEND: Datos convertidos para backend:', backendData);
    console.log('🛒 SOLO BACKEND: JSON que se enviará:', JSON.stringify(backendData, null, 2));
    
    // CORREGIDO: Usar endpoint correcto del backend con autenticación
    const response = await this.api.post<ApiResponse<any>>('/mobile/pedido', backendData);
    
    console.log('🛒 SOLO BACKEND: Respuesta del servidor:', response.data);
    
    if (response.data.success && response.data.data) {
      const orderData = response.data.data;
      
      console.log('🔍 Procesando respuesta del backend:', orderData);
      
      const processedOrder: Order = {
        id: orderData.pedido_id,
        orderNumber: orderData.numero_pedido || `ORD-${orderData.pedido_id}`,
        userId: orderData.cliente_id || 1,
        items: checkoutData.items.map((item: any) => ({
          product: {
            id: item.id_producto || item.product_id || 0,
            nombre: item.producto_nombre || item.nombre || 'Producto',
            precio: item.precio_unitario?.toString() || item.precio || '0',
            idempresa: 30,
            imagen: item.imagen || '',
            descripcion: '',
            monto_billetera: '0',
            stock: item.stock || 0,
            linea_id: 0,
            activo: true,
            visible_registrados: true,
            nivel_cliente: 1,
            created_at: '',
            categoria_id: 0,
            estado: 1,
            updated_at: ''
          },
          quantity: item.cantidad || item.quantity || 1,
          subtotal: parseFloat((item.precio_unitario || item.precio || 0).toString()) * (item.cantidad || item.quantity || 1),
          total: parseFloat((item.precio_unitario || item.precio || 0).toString()) * (item.cantidad || item.quantity || 1)
        })),
        total: parseFloat(orderData.total?.toString() || checkoutData.total.toString()),
        subtotal: parseFloat((checkoutData.subtotal || checkoutData.total).toString()),
        taxes: parseFloat((checkoutData.taxes || 0).toString()),
        shipping: parseFloat((checkoutData.shipping || 0).toString()),
        status: (orderData.estado as any) || 'pendiente',
        paymentStatus: 'pendiente',
        paymentMethod: {
          id_formapago: backendData.payment_method_id || 1,
          descripcion: 'Método de pago',
          estado: 1,
          idempresa: 1
        },
        shippingAddress: {
          name: backendData.shipping_data.name,
          address: backendData.shipping_data.address,
          city: backendData.shipping_data.city,
          phone: backendData.shipping_data.phone,
          department: backendData.shipping_data.department,
          municipio: backendData.shipping_data.municipio,
          references: backendData.shipping_data.references
        },
        createdAt: new Date().toISOString(),
        estimatedDelivery: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      };
      
      console.log('✅ SOLO BACKEND: Pedido procesado exitosamente:', processedOrder);
      return processedOrder;
    }
    
    throw new Error(response.data.message || 'Error al procesar el pedido');
  }

  // Método simplificado para crear pedidos directamente desde el carrito
  async createOrder(orderData: { items: any[], total: number, payment_method_id?: number }): Promise<any> {
    console.log('🛒 Creando pedido simple:', orderData);
    
    const response = await this.api.post<ApiResponse<any>>('/mobile/pedido', orderData);
    
    console.log('📦 Respuesta de creación de pedido:', response.data);
    
    if (response.data.success) {
      return response.data;
    }
    
    throw new Error(response.data.message || 'Error al crear el pedido');
  }

  // MÉTODOS DE PAGO
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    console.log('💳 SOLO BACKEND: Obteniendo métodos de pago');
    
    const response = await this.api.get<ApiResponse<PaymentMethod[]>>('/payment-methods');
    
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    
    throw new Error('Error al obtener métodos de pago');
  }

  // BILLETERA VIRTUAL
  async getWalletBalance(): Promise<{ saldo_billetera: string; moneda: string }> {
    console.log('💰 SOLO BACKEND: Obteniendo saldo de billetera virtual');
    
    try {
      const response = await this.api.get('/mobile/wallet/balance');
      
      if (response.data.success && response.data.data) {
        console.log('💰 SOLO BACKEND: Saldo obtenido:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error al obtener saldo de billetera');
    } catch (error) {
      console.error('❌ Error al obtener saldo de billetera:', error);
      throw error;
    }
  }

  // ========================
  // CARRITO PERSISTENTE
  // ========================

  // Obtener carrito del usuario
  async getCart(): Promise<any> {
    console.log('🛒 APIService: Obteniendo carrito del servidor');
    
    try {
      const response = await this.api.get('/mobile/carrito', {
        timeout: 15000, // Aumentar timeout a 15 segundos para el carrito
      });
      
      if (response.data.success && response.data.data) {
        console.log('🛒 APIService: Carrito obtenido:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error obteniendo carrito');
    } catch (error: any) {
      // Si es timeout o error de red, fallar silenciosamente
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        console.warn('⚠️ Timeout o error de red al obtener carrito, usando caché');
      } else {
        console.error('❌ Error obteniendo carrito:', error);
      }
      throw error;
    }
  }

  // Agregar producto al carrito
  async addToCart(productId: number, quantity: number, notas?: string): Promise<any> {
    console.log('🛒 APIService: Agregando al carrito - Producto:', productId, 'Cantidad:', quantity);
    
    try {
      const requestData = {
        id_producto: productId,
        cantidad: quantity,
        notas: notas
      };

      const response = await this.api.post('/mobile/carrito/agregar', requestData);
      
      if (response.data.success) {
        console.log('✅ Producto agregado al carrito:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error agregando al carrito');
    } catch (error) {
      console.error('❌ Error agregando al carrito:', error);
      throw error;
    }
  }

  // Actualizar cantidad en el carrito
  async updateCartQuantity(cartId: number, quantity: number): Promise<any> {
    console.log('🛒 APIService: Actualizando cantidad - Cart ID:', cartId, 'Cantidad:', quantity);
    
    try {
      const response = await this.api.put(`/mobile/carrito/actualizar/${cartId}`, {
        cantidad: quantity
      });
      
      if (response.data.success) {
        console.log('✅ Cantidad actualizada:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error actualizando cantidad');
    } catch (error) {
      console.error('❌ Error actualizando cantidad:', error);
      throw error;
    }
  }

  // Eliminar producto del carrito
  async removeFromCart(cartId: number): Promise<any> {
    console.log('🛒 APIService: Eliminando del carrito - Cart ID:', cartId);
    
    try {
      const response = await this.api.delete(`/mobile/carrito/eliminar/${cartId}`);
      
      if (response.data.success) {
        console.log('✅ Producto eliminado del carrito:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error eliminando del carrito');
    } catch (error) {
      console.error('❌ Error eliminando del carrito:', error);
      throw error;
    }
  }

  // Vaciar carrito completo
  async clearCart(): Promise<any> {
    console.log('🛒 APIService: Vaciando carrito completo');
    
    try {
      const response = await this.api.delete('/mobile/carrito/vaciar');
      
      if (response.data.success) {
        console.log('✅ Carrito vaciado:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error vaciando carrito');
    } catch (error) {
      console.error('❌ Error vaciando carrito:', error);
      throw error;
    }
  }

  // Sincronizar carrito local con servidor
  async syncCart(localItems: Array<{ id_producto: number; cantidad: number }>): Promise<any> {
    console.log('🛒 APIService: Sincronizando carrito - Items locales:', localItems.length);
    
    try {
      const response = await this.api.post('/mobile/carrito/sincronizar', {
        items_locales: localItems
      });
      
      if (response.data.success) {
        console.log('✅ Carrito sincronizado:', response.data.data);
        return response.data.data;
      }
      
      throw new Error(response.data.message || 'Error sincronizando carrito');
    } catch (error) {
      console.error('❌ Error sincronizando carrito:', error);
      throw error;
    }
  }

  // Obtener configuración de empresa (WhatsApp, teléfono, etc)
  async getEmpresaConfig(): Promise<{
    whatsapp: string;
    telefono: string;
    email: string;
    direccion: string;
    nombre_empresa: string;
  }> {
    console.log('🏢 APIService: Obteniendo configuración de empresa');
    
    try {
      const response = await this.api.get<ApiResponse<any>>('/mobile/configuracion');
      
      if (response.data.success && response.data.data) {
        console.log('✅ Configuración obtenida:', response.data.data);
        return response.data.data;
      }
      
      // Valores por defecto si falla
      return {
        whatsapp: '50212345678',
        telefono: '2345-6789',
        email: 'info@motomarket.com.gt',
        direccion: 'Guatemala',
        nombre_empresa: 'MotoMarketGT'
      };
    } catch (error) {
      console.error('❌ Error obteniendo configuración:', error);
      // Retornar valores por defecto en caso de error
      return {
        whatsapp: '50212345678',
        telefono: '2345-6789',
        email: 'info@motomarket.com.gt',
        direccion: 'Guatemala',
        nombre_empresa: 'MotoMarketGT'
      };
    }
  }

  async sendOrderConfirmationEmail(
    pedidoNumero: string,
    clienteEmail: string,
    pedidoDetalles: {
      cliente_nombre: string;
      fecha: string;
      productos_html: string;
      subtotal: string;
      impuestos: string;
      envio: string;
      total: string;
      direccion: string;
    }
  ): Promise<{
    success: boolean;
    enviado_cliente: boolean;
    enviado_empresa: boolean;
    message?: string;
  }> {
    console.log('📧 APIService: Enviando correos de confirmación del pedido');
    
    try {
      const response = await this.api.post<{
        success: boolean;
        message: string;
        enviado_cliente: boolean;
        enviado_empresa: boolean;
      }>('/mobile/pedido/enviar-correo', {
        pedido_numero: pedidoNumero,
        cliente_email: clienteEmail,
        pedido_detalles: pedidoDetalles
      });
      
      if (response.data.success) {
        console.log('✅ Correos enviados exitosamente');
        return {
          success: true,
          enviado_cliente: response.data.enviado_cliente || false,
          enviado_empresa: response.data.enviado_empresa || false
        };
      }
      
      return {
        success: false,
        enviado_cliente: false,
        enviado_empresa: false,
        message: response.data.message || 'Error desconocido'
      };
    } catch (error: any) {
      console.error('❌ Error enviando correos:', error);
      return {
        success: false,
        enviado_cliente: false,
        enviado_empresa: false,
        message: error.message || 'Error al enviar correos'
      };
    }
  }

  // Verificar si un correo ya existe
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const response = await this.api.get(`/check-email?email=${encodeURIComponent(email)}`);
      return response.data.exists || false;
    } catch (error) {
      console.error('❌ Error verificando correo:', error);
      return false;
    }
  }

  // Verificar si un teléfono ya existe
  async checkPhoneExists(phone: string): Promise<boolean> {
    try {
      const response = await this.api.get(`/check-phone?phone=${encodeURIComponent(phone)}`);
      return response.data.exists || false;
    } catch (error) {
      console.error('❌ Error verificando teléfono:', error);
      return false;
    }
  }
}

export default new APIService();
